import { el, svg } from "../core/dom";
import { SELECTION_CONTROL_ACCENT } from "../constants";
import { noticeChromeCopy, savedAnswerLabel } from "./chromeCopy";
import type {
  SelectionControlProps,
  SelectionControlRefs,
  SelectionControlSize,
} from "../types";

const SIZES = {
  large: {
    option: { fontSize: "14px", fontWeight: "500" },
    radio: { width: "17px", height: "17px" },
    select: { fontSize: "13px", padding: "6px 8px", fontWeight: "500" },
    slot: "68px",
    gap: "10px",
    tick: { box: 20, glyph: 12 },
  },
  small: {
    option: { fontSize: "12px", fontWeight: "400" },
    radio: { width: "13px", height: "13px" },
    select: { fontSize: "12px", padding: "5px 8px", fontWeight: "400" },
    slot: "62px",
    gap: "8px",
    tick: { box: 17, glyph: 10 },
  },
} as const;

const groupStyle = {
  display: "flex",
  alignItems: "center",
  flexShrink: "0",
};

const optionStyle = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const dropdownWrapStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-end",
  flexShrink: "0",
};

const settledAnswerStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-end",
  color: "#667085",
  whiteSpace: "nowrap",
};

const selectStyle = {
  border: "1px solid #d0d5dd",
  borderRadius: "6px",
  lineHeight: "18px",
  boxSizing: "border-box",
  cursor: "pointer",
  backgroundColor: "#ffffff",
  color: "#344054",
  flexShrink: "0",
};

const lockedBadgeStyle = (box: number) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: `${box}px`,
  height: `${box}px`,
  borderRadius: "50%",
  flexShrink: "0",
  boxSizing: "border-box",
  backgroundColor: "#10B981",
  marginLeft: "12px",
});

const lockedTick = (label: string | undefined, box: number, glyph: number) =>
  el(
    "div",
    { role: "img", "aria-label": label, style: lockedBadgeStyle(box) },
    svg(
      "svg",
      {
        width: String(glyph),
        height: String(glyph),
        viewBox: "0 0 12 12",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        "aria-hidden": "true",
      },
      svg("path", {
        d: "M4 6L5.5 7.5L8.5 4.5",
        stroke: "white",
        "stroke-width": "2",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
      })
    )
  );

const checkboxClass = (size: SelectionControlSize, className?: string) =>
  className ??
  (size === "small" ? "redacto-checkbox-medium" : "redacto-checkbox-large");

export function renderSelectionControl(
  props: SelectionControlProps
): SelectionControlRefs {
  const {
    control,
    onSelect,
    label,
    groupName,
    className,
    locked = false,
    lockedLabel,
    recordedAnswer,
    size = "large",
    language,
    skin,
  } = props;
  const copy = noticeChromeCopy(language);
  const options = [
    { label: copy.yes, value: true },
    { label: copy.no, value: false },
  ] as const;
  const scale = SIZES[size];
  let checked = props.checked;
  let indeterminate = props.indeterminate ?? false;

  const select = (next: boolean) => {
    if (indeterminate || next !== checked) onSelect(next);
  };

  const held = (value: boolean) =>
    locked ? value : !indeterminate && checked === value;

  const optionLabel = (text: string, value: boolean) =>
    recordedAnswer === value ? `${text} ${savedAnswerLabel(copy, text)}` : text;

  if (locked && control === "checkbox") {
    return {
      element: lockedTick(lockedLabel, scale.tick.box, scale.tick.glyph),
      setChecked: () => {},
    };
  }

  if (control === "radio") {
    const paintRadios = () => {
      radios.forEach((radio) => {
        radio.input.checked = held(radio.value);
      });
    };
    const radios = options.map((option) => ({
      value: option.value,
      input: el("input", {
        type: "radio",
        name: groupName,
        checked: held(option.value),
        disabled: locked,
        "aria-label": optionLabel(option.label, option.value),
        style: {
          accentColor: skin?.accent ?? SELECTION_CONTROL_ACCENT,
          ...scale.radio,
        },
        onChange: () => {
          select(option.value);
          paintRadios();
        },
      }) as HTMLInputElement,
      label: option.label,
    }));

    const element = el(
      "div",
      {
        role: "radiogroup",
        "aria-label": locked ? lockedLabel : label,
        style: { ...groupStyle, gap: scale.gap },
      },
      ...radios.map((radio) =>
        el(
          "label",
          { style: { ...optionStyle, ...scale.option } },
          radio.input,
          radio.label
        )
      )
    );

    return {
      element,
      setChecked: (next: boolean, nextIndeterminate = false) => {
        if (locked) return;
        checked = next;
        indeterminate = nextIndeterminate;
        paintRadios();
      },
    };
  }

  if (control === "dropdown") {
    if (locked) {
      return {
        element: el(
          "span",
          {
            role: "img",
            "aria-label": lockedLabel,
            style: { ...settledAnswerStyle, ...scale.select, width: scale.slot },
          },
          options[0].label
        ),
        setChecked: () => {},
      };
    }

    const mixedOption = el(
      "option",
      { value: "", disabled: true },
      copy.mixed
    ) as HTMLOptionElement;

    const selectEl = el(
      "select",
      {
        "aria-label": label,
        style: { ...selectStyle, ...scale.select, ...skin?.select, width: "100%" },
        onChange: () => {
          select(selectEl.value === "true");
          paint();
        },
      },
      ...options.map((option) =>
        el("option", { value: String(option.value) }, option.label)
      )
    ) as HTMLSelectElement;

    const paint = () => {
      if (indeterminate && mixedOption.parentNode !== selectEl) {
        selectEl.insertBefore(mixedOption, selectEl.firstChild);
      }
      if (!indeterminate && mixedOption.parentNode === selectEl) {
        selectEl.removeChild(mixedOption);
      }
      selectEl.value = indeterminate ? "" : String(held(true));
    };
    paint();

    return {
      element: el(
        "span",
        { style: { ...dropdownWrapStyle, width: scale.slot } },
        selectEl
      ),
      setChecked: (next: boolean, nextIndeterminate = false) => {
        checked = next;
        indeterminate = nextIndeterminate;
        paint();
      },
    };
  }

  const checkbox = el("input", {
    className: checkboxClass(size, className),
    type: "checkbox",
    checked,
    "aria-label": label,
    onChange: () => {
      select(!checked);
      checkbox.checked = checked;
      checkbox.indeterminate = indeterminate;
    },
  }) as HTMLInputElement;
  checkbox.indeterminate = indeterminate;

  return {
    element: checkbox,
    setChecked: (next: boolean, nextIndeterminate = false) => {
      checked = next;
      indeterminate = nextIndeterminate;
      checkbox.checked = next;
      checkbox.indeterminate = nextIndeterminate;
    },
  };
}
